// @workflow assistant.reply-blocks
/**
 * The shared value types of reply blocks (contract "Reply blocks", "Shared value types"): table
 * cells and record fields carry a `type`, the server sends raw values, and the UI checks and
 * formats them here, in one place. A value that doesn't match its type fails the block: the
 * model never types a number or a status the UI would then show as data.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { BLOCK_LIMITS, BLOCK_LOCALE, BLOCK_TEXT, REF_KINDS, TONE, TONES, VALUE_TYPE } from './blockContract.js';

export const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
export const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value);
const isAbsent = (value) => value === undefined || value === null;

/** A trimmed label within the limit, or null. */
export function cleanLabel(value, max = BLOCK_LIMITS.MAX_LABEL_LENGTH) {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  return text && text.length <= max ? text : null;
}

/** A tone: absent → `fallback`, one of the five → itself, anything else → undefined (refused). */
export function cleanTone(value, fallback = TONE.NEUTRAL) {
  if (isAbsent(value)) return fallback;
  return TONES.includes(value) ? value : undefined;
}

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A date-only value is a calendar day in the user's zone; anything else is an ISO date-time. */
function parseDate(value) {
  if (typeof value !== 'string') return null;
  const day = value.match(DATE_ONLY);
  const date = day ? new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3])) : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

const REFUSED = undefined;

const CLEANERS = Object.freeze({
  [VALUE_TYPE.TEXT]: (v) => (typeof v === 'string' ? v : REFUSED),
  [VALUE_TYPE.NUMBER]: (v) => {
    if (isFiniteNumber(v)) return { value: v, tone: null };
    if (!isPlainObject(v) || !isFiniteNumber(v.value)) return REFUSED;
    const tone = cleanTone(v.tone, null);
    return tone === undefined ? REFUSED : { value: v.value, tone };
  },
  [VALUE_TYPE.DATE]: (v) => (parseDate(v) ? v : REFUSED),
  [VALUE_TYPE.DATETIME]: (v) => (parseDate(v) ? v : REFUSED),
  [VALUE_TYPE.STATUS]: (v) => {
    const label = isPlainObject(v) ? cleanLabel(v.label) : null;
    const tone = label ? cleanTone(v.tone) : undefined;
    return label && tone ? { label, tone } : REFUSED;
  },
  [VALUE_TYPE.REF]: (v) => {
    const label = isPlainObject(v) ? cleanLabel(v.label) : null;
    if (!label || !REF_KINDS.includes(v.kind) || typeof v.id !== 'string' || !v.id) return REFUSED;
    return { label, kind: v.kind, id: v.id, facilityId: typeof v.facilityId === 'string' && v.facilityId ? v.facilityId : null };
  },
  [VALUE_TYPE.USER]: (v) => {
    const name = isPlainObject(v) ? cleanLabel(v.name) : null;
    return name ? { name } : REFUSED;
  },
  [VALUE_TYPE.PROGRESS]: (v) => (isFiniteNumber(v) && v >= 0 && v <= 1 ? v : REFUSED),
  [VALUE_TYPE.BOOLEAN]: (v) => (typeof v === 'boolean' ? v : REFUSED),
});

/**
 * @param {string} type a VALUE_TYPE
 * @param {unknown} raw
 * @returns {{ ok: true, value: unknown } | { ok: false }} an absent value is ok (null, drawn as a dash)
 */
export function cleanValue(type, raw) {
  const clean = Object.prototype.hasOwnProperty.call(CLEANERS, type) ? CLEANERS[type] : null;
  if (!clean) return { ok: false };
  if (isAbsent(raw)) return { ok: true, value: null };
  const value = clean(raw);
  return value === REFUSED ? { ok: false } : { ok: true, value };
}

// ---- writing values ---------------------------------------------------------------------------

const numberFormats = new Map();
function numberFormat(decimals) {
  if (!numberFormats.has(decimals)) {
    numberFormats.set(decimals, new Intl.NumberFormat(BLOCK_LOCALE, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }));
  }
  return numberFormats.get(decimals);
}

/** Three-letter months, as the app writes them ("28 Sep"): newer ICU writes "Sept" for en-GB. */
const MONTHS = Object.freeze(['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']);
const pad2 = (n) => String(n).padStart(2, '0');
const MINUS = '−';
const PERCENT = 100;

export const formatNumber = (value, decimals = 0) => numberFormat(decimals).format(value);

/** "+3", "−1", "0": a delta or a difference. */
export function formatSigned(value, decimals = 0) {
  if (value > 0) return `+${formatNumber(value, decimals)}`;
  if (value < 0) return `${MINUS}${formatNumber(-value, decimals)}`;
  return formatNumber(0, decimals);
}

/** "14 Oct 2026" in the user's time zone (a date-only value is that calendar day). */
export function formatDate(iso) {
  const d = parseDate(iso);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** "14 Oct, 12:05" in the user's time zone. */
export function formatDateTime(iso) {
  const d = parseDate(iso);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}, ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
export const formatProgress = (value) => `${Math.round(value * PERCENT)}%`;

/**
 * A number cell or field as written: "1.4 mm", "+3" (`signed`), "1,079".
 * @param {{ unit?: string, decimals?: number, signed?: boolean }} column
 */
export function numberText(column, value) {
  const decimals = column.decimals || 0;
  const text = column.signed ? formatSigned(value, decimals) : formatNumber(value, decimals);
  return column.unit ? `${text} ${column.unit}` : text;
}

/** The text of a cleaned value, as the UI writes it (cells, fields, tooltips, screen readers). */
export function valueText(column, value) {
  if (isAbsent(value)) return BLOCK_TEXT.NONE;
  switch (column.type) {
    case VALUE_TYPE.NUMBER: return numberText(column, value.value);
    case VALUE_TYPE.DATE: return formatDate(value);
    case VALUE_TYPE.DATETIME: return formatDateTime(value);
    case VALUE_TYPE.STATUS:
    case VALUE_TYPE.REF: return value.label;
    case VALUE_TYPE.USER: return value.name;
    case VALUE_TYPE.PROGRESS: return formatProgress(value);
    case VALUE_TYPE.BOOLEAN: return value ? BLOCK_TEXT.YES : BLOCK_TEXT.NO;
    default: return String(value);
  }
}
